import { useCallback, useEffect, useMemo, useState } from "react";

import { GET_ALL_EMPLOYEES_NAME } from "../../../../config/Endpoints.js";
import { getApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import { useSqeEmployeeNameOptions } from "../../../hooks/use-sqe-employee-name-options.ts";
import type { EmployeeOption, EmployeeRow } from "./types.ts";

function unwrapDataArray(payload: unknown): unknown[] {
  if (payload && typeof payload === "object" && "data" in payload) {
    const inner = (payload as { data: unknown }).data;
    return Array.isArray(inner) ? inner : [];
  }
  return Array.isArray(payload) ? payload : [];
}

function toEmployeeOptions(rows: EmployeeRow[]): EmployeeOption[] {
  return rows.map((row) => {
    const baseName =
      row.empName ||
      [row.firstName, row.lastName].filter(Boolean).join(" ") ||
      String(row.id);
    const type = (row.empType ?? row.employeeType)?.trim();
    return {
      value: String(row.id),
      label: type ? `${baseName} (${type})` : baseName,
        empType: type,
    };
  });
}

export function useLeaveAttendanceEmployees(isSqe = false) {
  const sqeEmployees = useSqeEmployeeNameOptions(isSqe);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployees = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApi<unknown>(
        GET_ALL_EMPLOYEES_NAME,
        accessToken(),
        "json",
        signal,
      );
      const rows = unwrapDataArray(data) as EmployeeRow[];
      setEmployees(rows);
      setEmployeeOptions(toEmployeeOptions(rows));
    } catch {
      setEmployees([]);
      setEmployeeOptions([]);
      setError("Failed to load employees.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSqe) return;
    const ac = new AbortController();
    void fetchEmployees(ac.signal);
    return () => ac.abort();
  }, [fetchEmployees, isSqe]);

  const sqeRows = useMemo<EmployeeRow[]>(() => sqeEmployees.options.map((option) => ({
    id: option.value,
    empName: option.label,
    empType: "SQELABS",
  })), [sqeEmployees.options]);
  const sqeOptions = useMemo<EmployeeOption[]>(() => sqeEmployees.options.map((option) => ({
    ...option,
    empType: "SQELABS",
  })), [sqeEmployees.options]);

  return isSqe
    ? { employees: sqeRows, employeeOptions: sqeOptions, loading: sqeEmployees.loading, error: sqeEmployees.error, refetch: fetchEmployees }
    : { employees, employeeOptions, loading, error, refetch: fetchEmployees };
}

export function extractMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

